import { generateClient } from 'aws-amplify/api';
import * as queries from '../graphql/queries';
import * as mutations from '../graphql/mutations';
import * as APITypes from '../API';

type GeneratedQuery<InputType, OutputType> = string & {
  __generatedQueryInput: InputType;
  __generatedQueryOutput: OutputType;
};

const customGraphqlQueries = {
  getPublicSpace: `
    query GetSpace($id: ID!) {
      getSpace(id: $id) {
        title
        shortDescription
        whiteLabelElements {
          primaryMainColor
          backgroundStyle
          backgroundColor
          backgroundGradientColor
          iconUrl
          splashImageUrl
          myDayBackgroundImageUrl
          cardBackgroundColor
          desktopBannerImageUrl
          termsUrl
          privacyPolicyUrl
          primaryFontColor
          navBarFontColor
          faviconImages {
            sizeDescriptor
            imageUrl
          }
        }
        landingPageContent
        landingPageStyle {
          backgroundColor
        }
        landingPageCoverImageUrl
        sharedProgrammeConfiguration {
          sharedProgrammeCategories {
            categoryTag
            orderedProgrammeIDs
          }
        }
        productPreviewSections {
          title
          productPreviews {
            productType
            productObjectID
            title
            shortDescription
            coverImageUrl
            availableInMembershipTierIDs
          }
        }
        spaceSharedContentBoardIDs
        welcomeVideo {
          videoID
          title
          description
          thumbnailImageUrl
        }
        myDayScreenSections {
          publishedSectionType
          spaceContentLaneID
        }
        recipesMenuTitles
        chatConfig {
          appID
          appRegion
        }
        appStoreLinks {
          appStoreID
          appUrl
        }
        sharedRecipesBoardIDs
        defaultSharedProgrammeIDForNewUsers
        subscriptionBenefitsContent
        subscriptionPageStyle {
          backgroundColor
        }
        publishedSearchTags {
          id
          tag
        }
        personalisedNutritionEnabled
        id
        createdAt
        updatedAt
        owner
      }
    }
  `,
  listSpaceIAPSubscriptions: `
      query ListSpaceIAPSubscriptions(
        $filter: ModelSpaceIAPSubscriptionFilterInput
        $limit: Int
        $nextToken: String
      ) {
        listSpaceIAPSubscriptions(
          filter: $filter
          limit: $limit
          nextToken: $nextToken
        ) {
          items {
            spaceID
            productID
            title
            shortDescription
            currencyCode
            priceStr
            id
            bundleID
            duration
            signUpButtonLabelOverride
            appStoreID
            rank
          }
          nextToken
          __typename
        }
      }
    `,
  listDistilledMealsByParent: `query MealBySmorgBoard(
      $smorgBoardID: ID!
      $sortDirection: ModelSortDirection
      $filter: ModelMealFilterInput
      $limit: Int
      $nextToken: String
    ) {
      mealBySmorgBoard(
        smorgBoardID: $smorgBoardID
        sortDirection: $sortDirection
        filter: $filter
        limit: $limit
        nextToken: $nextToken
      ) {
        items {
          id
          schemaVersion
          smorgBoardID
          recipes {
            title
            shortDescription
            imageUrl
            recipeUrl
            publisher
            chef
            mealiqId
            nutrition {
              calories
              protein
              carbohydrate
              fat
              fibre
              sugar
              saturates
              salt
              veg_weight
              cholesterol
              calcium
              iron
              magnesium
              potassium
              vitamin_a
              vitamin_b1
              vitamin_b2
              vitamin_b3
              vitamin_b5
              vitamin_b6
              vitamin_b12
              vitamin_c
              vitamin_d
              vitamin_e
              vitamin_k
              phosphorous
              zinc
              copper
              manganese
              selenium
              folate
              omega3_dha
              omega3_dpa
              omega3_epa
              sucrose
              glucose
              fructose
              lactose
              maltose
            }
            ingredients {
              id
            }
            mealTypes
            tags
            supportedDiets
            servings
            cookTime
            prepTime
            totalTime
            recipeNotes
          }
          addons {
            name
          }
          additionallyReferredToBy {
            sourceType
            sourceID
          }
          origin {
            originObjectType
            originObjectID
          }
          derivedNutrition {
            totalNutritionPerServing {
              calories
              protein
              carbohydrate
              fat
              fibre
              sugar
              saturates
              salt
              veg_weight
              cholesterol
              calcium
              iron
              magnesium
              potassium
              vitamin_a
              vitamin_b1
              vitamin_b2
              vitamin_b3
              vitamin_b5
              vitamin_b6
              vitamin_b12
              vitamin_c
              vitamin_d
              vitamin_e
              vitamin_k
              phosphorous
              zinc
              copper
              manganese
              selenium
              folate
              omega3_dha
              omega3_dpa
              omega3_epa
              sucrose
              glucose
              fructose
              lactose
              maltose
            }
            ingredientNutrition {
              ingredientID
              resolvedNutrition
              error
            }
            foodAllergens
            foodPreferences
            status
            macroRatio {
              carbohydrate
              protein
              fat
            }
          }
          spaceMembershipID
          groups
          createdAt
          updatedAt
          owner
        }
        nextToken
      }
    }
    ` as GeneratedQuery<
    APITypes.MealBySmorgBoardQueryVariables,
    APITypes.MealBySmorgBoardQuery
  >,
  listDistilledSharedMealsByParent: `query SharedMealBySharedBoard(
    $sharedBoardID: ID!
    $sortDirection: ModelSortDirection
    $filter: ModelSharedMealFilterInput
    $limit: Int
    $nextToken: String
  ) {
    sharedMealBySharedBoard(
      sharedBoardID: $sharedBoardID
      sortDirection: $sortDirection
      filter: $filter
      limit: $limit
      nextToken: $nextToken
    ) {
      items {
        id
        schemaVersion
        sharedBoardID
        recipes {
          title
          shortDescription
          imageUrl
          recipeUrl
          publisher
          chef
          mealiqId
          nutrition {
            calories
            protein
            carbohydrate
            fat
            fibre
            sugar
            saturates
            salt
            veg_weight
            cholesterol
            calcium
            iron
            magnesium
            potassium
            vitamin_a
            vitamin_b1
            vitamin_b2
            vitamin_b3
            vitamin_b5
            vitamin_b6
            vitamin_b12
            vitamin_c
            vitamin_d
            vitamin_e
            vitamin_k
            phosphorous
            zinc
            copper
            manganese
            selenium
            folate
            omega3_dha
            omega3_dpa
            omega3_epa
            sucrose
            glucose
            fructose
            lactose
            maltose
          }
          mealTypes
          tags
          supportedDiets
          servings
          cookTime
          prepTime
          totalTime
          recipeNotes
        }
        origin {
          originObjectType
          originObjectID
        }
        derivedNutrition {
          totalNutritionPerServing {
            calories
            protein
            carbohydrate
            fat
            fibre
            sugar
            saturates
            salt
            veg_weight
            cholesterol
            calcium
            iron
            magnesium
            potassium
            vitamin_a
            vitamin_b1
            vitamin_b2
            vitamin_b3
            vitamin_b5
            vitamin_b6
            vitamin_b12
            vitamin_c
            vitamin_d
            vitamin_e
            vitamin_k
            phosphorous
            zinc
            copper
            manganese
            selenium
            folate
            omega3_dha
            omega3_dpa
            omega3_epa
            sucrose
            glucose
            fructose
            lactose
            maltose
          }
          foodAllergens
          foodPreferences
          status
          macroRatio {
            carbohydrate
            protein
            fat
          }
        }
        createdAt
        updatedAt
      }
      nextToken
    }
  }
  ` as GeneratedQuery<
    APITypes.SharedMealBySharedBoardQueryVariables,
    APITypes.SharedMealBySharedBoardQuery
  >,
};

const combinedGraphqlQueries = {
  ...queries,
  ...mutations,
  ...customGraphqlQueries,
};

const client = generateClient();

export enum QueryType {
  QUERY_BY_ID = 'QUERY_BY_ID',
  QUERY_BY_IDS = 'QUERY_BY_IDS',
  SCAN = 'SCAN',
  SCAN_BY_IDS = 'SCAN_BY_IDS',
  MUTATION = 'MUTATION',
}

/**
 * A generic GraphQL API
 */

// https://github.com/reduxjs/redux-toolkit/issues/1125#issuecomment-1293544711

const fetchPaginatedListGraphqlApi = async ({
  queryName,
  data,
  appSyncParams,
}: {
  queryName: keyof typeof combinedGraphqlQueries;
  data: any;
  appSyncParams: any;
}) => {
  try {
    const responseData = [] as any;
    let counter = 0;

    const paginateQuery = async (
      nextToken: string | null = null,
    ): Promise<void> => {
      if (counter > 0 && nextToken === null) {
        return Promise.resolve();
      }
      const response = (await client.graphql({
        query: combinedGraphqlQueries[queryName],
        variables: { ...data, nextToken },
        ...(appSyncParams || {}),
      })) as any;
      const responseDataKey = appSyncParams?.responseDataKey || queryName;
      const queryData = response.data[responseDataKey]?.items;
      const newNextToken = response.data[responseDataKey]?.nextToken;
      counter += 1;
      responseData.push(...queryData);
      return paginateQuery(newNextToken);
    };

    await paginateQuery();
    return responseData;
  } catch (err: any) {
    console.error('err while fetching', err);
    console.log('queryName err: ', queryName);
    console.log('data err: ', data);
    throw err;
  }
};

const getSingularDataGraphqlApi = async ({
  queryName,
  data,
  appSyncParams,
}: {
  queryName: keyof typeof combinedGraphqlQueries;
  data: any;
  appSyncParams: any;
}) => {
  try {
    const response = (await client.graphql({
      query: combinedGraphqlQueries[queryName],
      variables: { ...data },
      ...(appSyncParams || {}),
    })) as any;
    const responseDataKey = appSyncParams?.responseDataKey || queryName;
    const responseData = response.data[responseDataKey];
    return responseData;
  } catch (err: any) {
    console.error('err while fetching', err);
    console.log('queryName err: ', queryName);
    console.log('data err: ', data);
    throw err;
  }
};

const executeGraphqlApiMutation = async ({
  mutationName,
  data,
  appSyncParams,
}: {
  mutationName: keyof typeof combinedGraphqlQueries;
  data: any;
  appSyncParams: any;
}) => {
  try {
    const response = (await client.graphql({
      query: combinedGraphqlQueries[mutationName],
      variables: { ...data },
      ...(appSyncParams || {}),
    })) as any;
    const responseDataKey = appSyncParams?.responseDataKey || mutationName;
    const responseData = response.data[responseDataKey];
    return responseData;
  } catch (err: any) {
    console.error('err while fetching', err);
    console.log('mutationName err: ', mutationName);
    console.log('data err: ', data);
    throw err;
  }
};

export const graphqlBaseQuery =
  () =>
  async ({
    queryName,
    data,
    queryType,
    appSyncParams,
  }: {
    queryName: keyof typeof combinedGraphqlQueries;
    data: any;
    queryType: QueryType;
    appSyncParams?: any;
  }) => {
    try {
      let responseData;
      if (queryType === QueryType.SCAN) {
        responseData = await fetchPaginatedListGraphqlApi({
          queryName,
          data,
          appSyncParams,
        });
      } else if (queryType === QueryType.SCAN_BY_IDS) {
        const keyField = Object.keys(data)[0];
        const keyValues = data[keyField] as Array<string>;
        const promises = keyValues.map((keyValue) =>
          fetchPaginatedListGraphqlApi({
            queryName,
            data: { [keyField]: keyValue },
            appSyncParams,
          }),
        );
        const allResponseData = await Promise.all(promises);
        return { data: allResponseData };
      } else if (queryType === QueryType.QUERY_BY_IDS) {
        const keyField = Object.keys(data)[0];
        const keyValues = data[keyField] as Array<string>;
        const promises = keyValues.map((keyValue) =>
          getSingularDataGraphqlApi({
            queryName,
            data: { [keyField]: keyValue },
            appSyncParams,
          }),
        );
        const allResponseData = await Promise.all(promises);
        return { data: allResponseData };
      } else if (queryType === QueryType.MUTATION) {
        responseData = await executeGraphqlApiMutation({
          mutationName: queryName,
          data,
          appSyncParams,
        });
      } else {
        responseData = await getSingularDataGraphqlApi({
          queryName,
          data,
          appSyncParams,
        });
      }
      return { data: responseData };
    } catch (error) {
      return { error };
    }
  };
